import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Field, Input, Select } from "@/components/ui";
import { fetchSiteMatches, SiteMatchList, type SiteMatchCandidate } from "./siteMatch";

// =============================================================================
// SitePicker — reusable site selector with optional inline "+ New site" creator
// Used by NewProjectScreen (BID + project create). Keeps the caller in-flow —
// when a site doesn't exist yet, user creates it from a modal and the new
// site is auto-selected without leaving the form. T-037.
//
// 26 Oct 2026 (mig 451) — the quick-add gets the DUPLICATE CHECK it never had.
// Leaving the name field (and pressing Create) asks app.site_match_candidates
// whether the register already holds this place: an exact RFNSA / carrier site
// id, the same name once EXCHANGE / RT / CMTS are stripped, or a near-miss.
// Candidates are shown with "Use this site"; "Create anyway" is the way past.
// Nothing is linked without a person choosing it. The RFQ intake screen uses
// the same component (siteMatch.tsx), so both doorways behave alike.
// =============================================================================

interface SiteOption {
  site_id:     string;
  site_code:   string;
  site_name:   string;
  suburb:      string | null;
  state:       string | null;
  asset_owner: { owner_code: string } | null;
}

interface Props {
  value:       string;
  onChange:    (siteId: string) => void;
  allowCreate?: boolean;
  placeholder?: string;
}

export function SitePicker({ value, onChange, allowCreate = true, placeholder = "— select site —" }: Props) {
  const user = useRequireAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const canCreate = allowCreate && (user?.role === "director" || user?.role === "manager");

  const { data: sites = [] } = useQuery({
    queryKey: ["sites_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sites")
        .select("site_id, site_code, site_name, suburb, state, asset_owner:asset_owners(owner_code)")
        .eq("is_active", true)
        .order("site_name")
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as unknown as SiteOption[];
    },
  });

  return (
    <>
      <div className="flex gap-2">
        <Select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1"
        >
          <option value="">{placeholder}</option>
          {sites.map(s => {
            const locBits = [s.suburb, s.state].filter(Boolean).join(", ");
            const ownerBit = s.asset_owner?.owner_code ? ` [${s.asset_owner.owner_code}]` : "";
            return (
              <option key={s.site_id} value={s.site_id}>
                {s.site_name}{ownerBit}{locBits ? ` — ${locBits}` : ""}{s.site_code ? ` · ${s.site_code}` : ""}
              </option>
            );
          })}
        </Select>
        {canCreate && (
          <Button
            variant="secondary"
            size="md"
            icon={Plus}
            onClick={() => setModalOpen(true)}
            type="button"
          >
            New
          </Button>
        )}
      </div>

      {modalOpen && (
        <QuickCreateSiteModal
          onClose={() => setModalOpen(false)}
          onCreated={(newSiteId) => {
            onChange(newSiteId);
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// QuickCreateSiteModal — minimal inline create form for the BID/project flow.
// Only asks for site_name + core address. User can enrich later via /sites/:id.
// With the duplicate check (26 Oct 2026): candidates appear under the name once
// it is typed; "Use this site" selects the existing one without creating.
// -----------------------------------------------------------------------------

export function QuickCreateSiteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (siteId: string) => void;
}) {
  const qc = useQueryClient();
  const [site_name, setSiteName] = useState("");
  const [asset_owner_id, setAssetOwnerId] = useState("");
  const [address_line1, setAddr1] = useState("");
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // duplicate check state
  const [candidates, setCandidates] = useState<SiteMatchCandidate[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkedFor, setCheckedFor] = useState<string>("");      // what the last check looked at
  const [createAnyway, setCreateAnyway] = useState(false);

  const { data: assetOwners = [] } = useQuery({
    queryKey: ["asset_owners_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_owners")
        .select("asset_owner_id, owner_code, display_name")
        .eq("is_active", true)
        .order("display_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const checkKey = `${site_name.trim()}|${suburb.trim()}|${state.trim()}`;

  async function runCheck(): Promise<SiteMatchCandidate[]> {
    if (!site_name.trim()) { setCandidates([]); return []; }
    if (checkKey === checkedFor) return candidates;
    setChecking(true);
    try {
      const found = await fetchSiteMatches({ name: site_name, suburb, state });
      setCandidates(found);
      setCheckedFor(checkKey);
      setCreateAnyway(false);
      return found;
    } catch (e) {
      // the check failing must never block creating a site — say so quietly and move on
      setCandidates([]);
      setCheckedFor(checkKey);
      return [];
    } finally {
      setChecking(false);
    }
  }

  const createMut = useMutation({
    mutationFn: async () => {
      if (!site_name.trim()) throw new Error("Site name is required.");
      const { data, error } = await supabase
        .from("sites")
        .insert({
          site_name: site_name.trim(),
          asset_owner_id: asset_owner_id || null,
          address_line1: address_line1.trim() || null,
          suburb: suburb.trim() || null,
          state: state.trim() || null,
          postcode: postcode.trim() || null,
          is_active: true,
          site_code: "",
        })
        .select("site_id")
        .single();
      if (error) throw error;
      return data.site_id as string;
    },
    onSuccess: (siteId) => {
      qc.invalidateQueries({ queryKey: ["sites_picker"] });
      qc.invalidateQueries({ queryKey: ["sites_list"] });
      onCreated(siteId);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Failed to create site"),
  });

  // Create: run the check first if the name changed since the last one; if
  // candidates come back, stop and show them — the second press (Create anyway)
  // goes through.
  async function onCreateClick() {
    setErr(null);
    const found = await runCheck();
    if (found.length > 0 && !createAnyway) return;   // show them — the button becomes "Create anyway"
    createMut.mutate();
  }

  const hasCandidates = candidates.length > 0 && checkedFor === checkKey;
  const blocked = hasCandidates && !createAnyway;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <Card>
          <div className="p-5 border-b border-line flex items-center justify-between">
            <div>
              <div className="text-[14px] font-medium text-text">New site</div>
              <div className="text-[12px] text-text-muted">Quick create — enrich details later via the site page.</div>
            </div>
            <button onClick={onClose} className="text-text-muted hover:text-text" type="button">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-3">
            <Field label="Site name *">
              <Input
                autoFocus
                value={site_name}
                onChange={e => { setSiteName(e.target.value); setCreateAnyway(false); }}
                onBlur={() => { void runCheck(); }}
                placeholder="e.g. Toowoomba CBD rooftop"
              />
            </Field>
            {(checking || hasCandidates) && (
              <SiteMatchList
                candidates={hasCandidates ? candidates : []}
                loading={checking}
                onUse={(c) => {
                  qc.invalidateQueries({ queryKey: ["sites_picker"] });
                  onCreated(c.site_id);
                }}
              />
            )}
            <Field label="Asset owner" hint="Who owns the tower / pit / exchange.">
              <Select value={asset_owner_id} onChange={e => setAssetOwnerId(e.target.value)}>
                <option value="">— none —</option>
                {assetOwners.map(ao => (
                  <option key={ao.asset_owner_id} value={ao.asset_owner_id}>
                    {ao.owner_code} · {ao.display_name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Address">
              <Input
                value={address_line1}
                onChange={e => setAddr1(e.target.value)}
                placeholder="Street address"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Suburb">
                <Input value={suburb} onChange={e => setSuburb(e.target.value)} onBlur={() => { void runCheck(); }} />
              </Field>
              <Field label="State">
                <Input value={state} onChange={e => setState(e.target.value.toUpperCase())} onBlur={() => { void runCheck(); }} placeholder="QLD" maxLength={3} />
              </Field>
              <Field label="Postcode">
                <Input value={postcode} onChange={e => setPostcode(e.target.value)} maxLength={4} mono />
              </Field>
            </div>
            {err && (
              <div className="text-[12px] text-red-400 bg-red-900/20 border border-red-900/40 rounded px-3 py-2">
                {err}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-line flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            {blocked ? (
              <Button
                variant="secondary"
                onClick={() => { setCreateAnyway(true); createMut.mutate(); }}
                disabled={createMut.isPending}
                type="button"
                title="Create a new site record even though a similar one exists"
              >
                {createMut.isPending ? "Creating…" : "Create anyway"}
              </Button>
            ) : (
              <Button
                onClick={() => { void onCreateClick(); }}
                disabled={!site_name.trim() || createMut.isPending || checking}
                type="button"
              >
                {createMut.isPending ? "Creating…" : checking ? "Checking…" : "Create site"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
